import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchSubscriptionPlans,
  formatPrice,
  type SubscriptionPlan,
} from "@/services/subscriptionService";
import { PlanCard } from "@/features/subscription/components/PlanCard";

type Period = "monthly" | "yearly";

/**
 * Public pricing section for the landing page. Reads the publicly-readable
 * `subscription_plans` (anon fallback in the service — no auth), shows the
 * plans flagged `is_visible_on_landing`, and lets PlanCard route the CTA to
 * `/register?plan=<slug>`. Renders nothing if the data can't be loaded, so a
 * Supabase hiccup never breaks the landing page.
 */
export function PricingSection() {
  const { t } = useTranslation("pricing");
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [error, setError] = useState(false);
  const [period, setPeriod] = useState<Period>("monthly");
  const [apiOnlyPlan, setApiOnlyPlan] = useState<SubscriptionPlan | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchSubscriptionPlans()
      .then((all) => {
        if (cancelled) return;
        const visible = all
          .filter((p) => p.is_visible_on_landing && !p.is_deprecated)
          .sort((a, b) => (a.display_order ?? 999) - (b.display_order ?? 999));
        setApiOnlyPlan(all.find((p) => p.slug === "api_only" && !p.is_deprecated) ?? null);
        setPlans(visible);
      })
      .catch((e) => {
        if (cancelled) return;
        console.warn("PricingSection: failed to load plans", e);
        setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error || plans.length === 0) return null;

  const toggleBtn = (value: Period, label: string) => (
    <button
      type="button"
      onClick={() => setPeriod(value)}
      className={`rounded-full px-4 py-1.5 transition-colors ${
        period === value
          ? "bg-[#4CD6C4] text-[#070A0B]"
          : "text-[#788A82] hover:text-[#E2EDE7]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <section id="pricing" className="relative border-t border-[#1B2622]">
      <div className="relative mx-auto max-w-[1320px] px-6 py-24 lg:px-10 lg:py-28">
        <div className="flex flex-col gap-8 border-b border-[#1B2622] pb-10 lg:flex-row lg:items-end lg:justify-between">
          <h2 className="bp-display text-[34px] text-[#E2EDE7] sm:text-[44px]">
            {t("pricing.title")}
          </h2>
          <div className="inline-flex items-center gap-1 self-start rounded-full border border-[#1B2622] p-1 bp-mono text-[12px] uppercase tracking-wide">
            {toggleBtn("monthly", t("pricing.monthly"))}
            {toggleBtn("yearly", t("pricing.yearly"))}
          </div>
        </div>

        <div className="mt-12 grid items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => {
            const yearly = period === "yearly" && plan.price_yearly != null;
            return (
              <PlanCard
                key={plan.id}
                plan={plan}
                variant="landing"
                price={yearly ? (plan.price_yearly as number) : plan.price_monthly}
                pricePeriodLabel={yearly ? t("pricing.perYear") : t("pricing.perMonth")}
              />
            );
          })}
        </div>

        {apiOnlyPlan && (
          <div className="mt-10 rounded-xl border border-[#1B2622] bg-[#0B100F] p-6 sm:p-8">
            <p className="bp-mono text-[12px] uppercase tracking-[0.14em] text-[#4CD6C4]">
              {t("pricing.apiNote.title")}
            </p>
            <p className="mt-3 text-sm text-[#E2EDE7]">{t("pricing.apiNote.included")}</p>
            <p className="mt-2 text-sm text-[#788A82]">
              {t("pricing.apiNote.standalone", { price: formatPrice(apiOnlyPlan.price_monthly) })}
            </p>
            <p className="mt-2 text-sm text-[#788A82]">{t("pricing.apiNote.requirement")}</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default PricingSection;
